/**
 * The one clipboard write in the tree.
 *
 * Every surface that copies has the same two failure modes and neither of
 * them throws where the caller is looking. The API can be ABSENT, on an
 * insecure origin or an older browser, and it can REFUSE, when the document
 * has lost focus or the permission is denied. Written inline they read as
 * `navigator.clipboard?.writeText(x).then(...)`, which looks defensive and is
 * the opposite: the optional chain short-circuits the whole expression when
 * the API is missing, taking the rejection handler with it, so the button
 * does nothing and says nothing in exactly the case it is meant to cover.
 *
 * So the write lives here, answers what happened, and never rejects. A caller
 * that ignores the answer is then a visible mistake rather than an invisible
 * one, and `clipboard-call-sites.test.ts` keeps `navigator.clipboard` from
 * reappearing anywhere else.
 *
 * Plain TypeScript on purpose: `src/utils` must not pull React into anything
 * that imports it, so the crash screen (a class component with no provider
 * tree) and the window-level error handlers can both use it.
 */

/**
 * What became of a write.
 *
 * The two failures are kept apart because one surface acts on the
 * difference: a refused write is worth a "retry" button, since the usual
 * cause (the document lost focus between the press and the write) is gone by
 * the next click, while an absent API will still be absent, and offering a
 * retry there promises something the page cannot do.
 */
export type ClipboardResult = "written" | "unavailable" | "refused";

/**
 * Write `text` to the system clipboard.
 *
 * Never rejects, so a caller can `await` it without a `try`, and never
 * resolves to a bare `false` that hides which of the two failures happened.
 */
export async function writeClipboard(text: string): Promise<ClipboardResult> {
  let clipboard: Clipboard | undefined;
  try {
    if (typeof navigator === "undefined") return "unavailable";
    clipboard = navigator.clipboard as Clipboard | undefined;
    if (typeof clipboard?.writeText !== "function") return "unavailable";
  } catch {
    // Reading the property can itself throw behind a hardened or stubbed
    // navigator; an API we cannot even look at is an API we do not have.
    return "unavailable";
  }
  try {
    await clipboard.writeText(text);
    return "written";
  } catch {
    // No user activation, no document focus, permission denied, or a
    // synchronous throw from the implementation: the write did not land.
    return "refused";
  }
}
